import { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { first } from 'lodash';
// Import Types
import type { BonusDetail, NFTVoucherCollectionItem } from 'types';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import { useMenuItem } from 'hooks/UseMenuItem';
// Import Services
import { UtilsHttpService } from 'services/http';
// Import UI Components
import { BusyLoader, SkeletonLoader } from '@geneui/components';
// Import Components
import GeneralInfo from './GeneralInfo';
import BonusInfo from './BonusInfo';
// Import Constants
import { BonusTypesLabels, l } from 'constants/common';
// Import Enums
import { MenuItemIdEnum } from 'enums';

interface Props {
    id?: number;
    type?: number;
    title?: string;
    description?: string;
    startDate?: string;
    endDate?: string;
    bonusDetails?: Nullable<BonusDetail[]>;
    state?: number;
    wallet?: Nullable<string>;
    internalMessageTitle?: string;
    isLoading?: boolean;
    sectionTitle?: string;
}

const BonusInfoModalBody: FC<Props> = (props) => {
    const {
        id,
        title,
        description,
        type,
        startDate,
        endDate,
        bonusDetails,
        state,
        wallet = '',
        internalMessageTitle,
        isLoading,
        sectionTitle = null,
    } = props;
    const { t } = useTranslation();
    const { doGetRequest } = useRequest();

    const [isLoadingMarketplaces, setIsLoadingMarketplaces] = useState(true);
    const [marketplace, setMarketplace] = useState(true);
    const [nftVoucherCollections, setNftVoucherCollections] = useState<NFTVoucherCollectionItem[]>([]);
    const { data: wonderWheelsData } = useMenuItem(
        BonusTypesLabels[type ?? ''] === BonusTypesLabels.WonderWheelSpin ? MenuItemIdEnum.WonderWheel : undefined,
    );

    const { getNftMarketplacesRequest, getNftVoucherMarketplacesRequest, getNftVoucherCollectionsListRequest } =
        useCancelableRequests({
            getNftMarketplacesRequest: UtilsHttpService.getNftMarketplaces,
            getNftVoucherMarketplacesRequest: UtilsHttpService.getNftVoucherMarketplaces,
            getNftVoucherCollectionsListRequest: UtilsHttpService.getNftVoucherCollectionsList,
        });

    const selectedWonderWheel = useMemo(() => {
        if (type === BonusTypesLabels.WonderWheelSpin) {
            return wonderWheelsData.find((item) => item.Value === bonusDetails?.[0]?.Id);
        }
    }, [bonusDetails, wonderWheelsData, type]);

    const selectedNFTV = useMemo(() => {
        if (type === BonusTypesLabels.NftVoucherBonus) {
            return nftVoucherCollections.find((item) => item.CollectionId === bonusDetails?.[0]?.Id);
        }
    }, [bonusDetails, nftVoucherCollections, type]);

    const nftBonusBonusDetails = useMemo(() => first(bonusDetails), [bonusDetails]);

    useEffect(() => {
        if (type === BonusTypesLabels.NFTBonus) {
            doGetRequest(getNftMarketplacesRequest.request, {
                successCallback: (data) => {
                    const marketplaceValue = first(bonusDetails)?.Marketplace;
                    setMarketplace((data ?? []).find((item) => item.Value === marketplaceValue)?.DisplayName);
                },
            }).then(() => setIsLoadingMarketplaces(false));
        }

        if (type === BonusTypesLabels.NftVoucherBonus) {
            doGetRequest(getNftVoucherMarketplacesRequest.request, {
                successCallback: (data) => {
                    const marketplaceValue = first(bonusDetails)?.Marketplace;
                    setMarketplace((data ?? []).find((item) => item.Value === marketplaceValue)?.DisplayName);
                },
            }).then(() => setIsLoadingMarketplaces(false));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type]);

    const init = () => {
        if (type === BonusTypesLabels.NftVoucherBonus) {
            doGetRequest(getNftVoucherCollectionsListRequest.request, {
                successCallback: (data) => {
                    setNftVoucherCollections(data);
                },
            });
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <BusyLoader isBusy={isLoading} type="spinner" spinnerSize="medium">
            <div>
                <GeneralInfo
                    id={id}
                    title={title}
                    description={description}
                    type={type}
                    startDate={startDate}
                    endDate={endDate}
                    state={state}
                    walletType={wallet}
                    internalMessageTitle={internalMessageTitle}
                    wonderWheelName={selectedWonderWheel?.Name}
                    wonderWheelId={selectedWonderWheel?.Id}
                    nftVId={selectedNFTV?.CollectionId}
                    nftVName={selectedNFTV?.Name}
                    usageType={bonusDetails?.[0]?.Usage}
                    isLocalNetwork={bonusDetails?.[0]?.IsLocalNetwork}
                    sectionTitle={sectionTitle}
                />
                {[BonusTypesLabels.NFTBonus, BonusTypesLabels.NftVoucherBonus].includes(type ?? -1) && (
                    <div className="crm-object-modal-head">
                        <div className="key-value-h">
                            <span className="ellipsis-text kv-label">{t(l.Marketplace)}</span>
                            <SkeletonLoader
                                isBusy={isLoadingMarketplaces}
                                className="ellipsis-text kv-value kv-value-css"
                            >
                                {!isLoadingMarketplaces && (
                                    <span className="ellipsis-text kv-value kv-value-css">{marketplace}</span>
                                )}
                            </SkeletonLoader>
                        </div>
                        {BonusTypesLabels.NFTBonus === type && (
                            <>
                                <div className="key-value-h">
                                    <span className="ellipsis-text kv-label">{t(l.From)}</span>
                                    <span className="ellipsis-text kv-value kv-value-css">
                                        {nftBonusBonusDetails?.From}
                                    </span>
                                </div>

                                <div className="key-value-h">
                                    <span className="ellipsis-text kv-label">{t(l.To)}</span>
                                    <span className="ellipsis-text kv-value kv-value-css">
                                        {nftBonusBonusDetails?.To}
                                    </span>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
            {type !== BonusTypesLabels.NFTBonus && (
                <div className="crm-object-modal-bottom-1">
                    {/*For title text can add active-state class*/}
                    <div className="crm-object-modal-b-content c-s-b-content">
                        <BonusInfo bonusDetails={bonusDetails} type={type} />
                    </div>
                </div>
            )}
        </BusyLoader>
    );
};

export default BonusInfoModalBody;
